import { Platform, Image, View, StyleSheet } from 'react-native';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import HomeScreen from 'src/presentation/screens/HomeScreen';
import ArchiveScreen from 'src/presentation/screens/ArchiveScreen';
import MessagesScreen from 'src/presentation/screens/MessagesScreen';
import MessageIndicator from 'src/presentation/components/atoms/MessageIndicator';
import GalleryStackNavigator from 'src/presentation/navigation/navigators/GalleryStackNavigator';
import SettingsStackNavigator from 'src/presentation/navigation/navigators/SettingsStackNavigator';
import Constants from 'src/core/utils/Constants';
import Colors from 'src/core/utils/Colors';
import Fonts from 'src/core/utils/Fonts';
import Icons from 'src/core/utils/Icons';
import Images from 'src/core/utils/Images';
import Translations from 'src/core/utils/Translations';
import type { BottomTabParamList } from 'src/presentation/navigation/types/NavigationTypes';
import Dimens from 'src/core/utils/Dimens';

const Tab = createBottomTabNavigator<BottomTabParamList>();

/**
 * The bar's height on web, spelled out as the things it has to hold.
 *
 * Leaving the height off is not an option: react-native-web's bottom-tabs then falls back to 49px,
 * which is less than its own contents need, and the label is the flex child that gives way. Native
 * gets the slack from the bottom safe-area inset, which a browser does not have.
 */
const WEB_TAB_BAR_HEIGHT =
	Dimens.padding10 + // the bar's own top padding
	Dimens.padding5 * 2 + // the tab item's padding, top and bottom
	Dimens.iconSize28 + // the icon
	Dimens.margin6 +
	Dimens.margin4 + // the label's margins
	Dimens.lineHeight15 + // the label itself at 12px
	Dimens.padding12; // room under the icons

function BottomTabNavigator() {
	const insets = useSafeAreaInsets();
	// Extra breathing room under the labels on Android so the gesture pill does not crowd them (the gesture-nav
	// bottom inset is small).
	const extraBottom = Platform.OS === 'android' ? Dimens.padding8 : 0;

	return (
		<View style={styles.container}>
			<Tab.Navigator
				screenOptions={{
					headerShown: false,
					// Freeze non-focused tabs so they do not re-render during cross-tab store updates (e.g.
					freezeOnBlur: true,
					tabBarActiveTintColor: Colors.secondary,
					tabBarInactiveTintColor: Platform.OS === 'ios' ? Colors.greyText : Colors.greyTab,
					tabBarLabelStyle: styles.tabBarLabelStyle,
					tabBarStyle: [
						styles.tabBarStyle,
						// A browser has no bottom safe-area inset, and that inset is the slack the native bar
						// leans on: 56px minus this bar's own 10px of top padding leaves 46 for a 28px icon
						// plus a 15px label with 10px of margin around it. The label lost that fight
						// silently, so on web the tab bar had no text under its icons at all. Let the content
						// decide the height there instead of a number that assumes an inset that is absent.
						Platform.OS === 'web'
							? // flexShrink as well, because the bar is a flex child of the screen: a height it
								// is then allowed to be squeezed out of is no height at all, and the drawer is
								// only 722px tall.
								{ height: WEB_TAB_BAR_HEIGHT, paddingBottom: Dimens.padding12, flexShrink: 0 }
							: { height: 56 + insets.bottom + extraBottom, paddingBottom: insets.bottom + extraBottom },
					],
				}}
			>
				<Tab.Screen
					component={HomeScreen}
					name={Constants.screenHome}
					options={{
						tabBarLabel: Translations.tabHome,
						tabBarIcon: ({ color, focused }) => (
							<Image
								source={Images.topicusLogo}
								style={{
									width: 30.48,
									height: 20.61,
									tintColor: focused ? color : Platform.OS === 'ios' ? Colors.greyText : Colors.greyTab,
								}}
								tintColor={color}
							/>
						),
					}}
				/>
				<Tab.Screen
					component={ArchiveScreen}
					name={Constants.screenArchive}
					options={{
						tabBarLabel: Translations.tabArchive,
						tabBarIcon: ({ color, focused }) =>
							Platform.OS === 'ios' ? (
								<View style={styles.archiveIconIos}>
									<Ionicons color={color} name={focused ? 'folder' : 'folder-outline'} size={26} />
								</View>
							) : (
								<Image source={Icons.archiveAndroid} tintColor={color} />
							),
					}}
				/>
				<Tab.Screen
					component={MessagesScreen}
					name={Constants.screenMessages}
					options={{
						tabBarLabel: Translations.tabMessages,
						tabBarIcon: ({ color, focused }) => (
							<View>
								<MessageIndicator />
								{Platform.OS === 'ios' ? (
									<Image
										resizeMode="contain"
										source={focused ? Icons.iconNotificationsFocus : Icons.iconNotifications}
										style={{ width: 31 }}
										tintColor={color}
									/>
								) : (
									<Image source={Icons.iconNotificationsAndroid} style={{ marginTop: -1 }} tintColor={color} />
								)}
							</View>
						),
					}}
				/>
				<Tab.Screen
					component={GalleryStackNavigator}
					name={Constants.screenPhotos}
					options={{
						tabBarLabel: Translations.tabPhotos,
						tabBarIcon: ({ color, focused }) =>
							Platform.OS === 'ios' ? (
								<Image source={focused ? Icons.cameraIosFocused : Icons.cameraIos} tintColor={color} />
							) : (
								<Image source={Icons.cameraAndroid} tintColor={color} />
							),
					}}
				/>
				<Tab.Screen
					component={SettingsStackNavigator}
					name={Constants.screenSettings}
					options={{
						tabBarLabel: Translations.tabSettings,
						tabBarIcon: ({ color, focused }) =>
							Platform.OS === 'ios' ? (
								<Image source={focused ? Icons.settingsIosFocused : Icons.settingsIos} tintColor={color} />
							) : (
								<Image source={Icons.settingsAndroid} tintColor={color} />
							),
					}}
				/>
			</Tab.Navigator>
		</View>
	);
}

const styles = StyleSheet.create({
	container: {
		flex: 1,
	},
	// Fixed, centered footprint so the (taller) Ionicons glyph aligns with the image-based tab icons.
	archiveIconIos: {
		height: 24,
		justifyContent: 'center',
		alignItems: 'center',
	},
	tabBarLabelStyle: {
		fontFamily: Fonts.montserratMedium,
		fontSize: Dimens.fontSize12,
		// Yoga never shrinks a text box, CSS does. Without this the label was the flex child that
		// gave way when the bar ran out of room on web: height 0 behind overflow:hidden, colour and
		// font intact, nothing on screen. Native already behaves this way, so this changes nothing
		// there.
		flexShrink: 0,
		marginTop: Dimens.margin6,
		marginBottom: Dimens.margin4,
		width: '100%',
	},
	tabBarStyle: {
		paddingTop: Dimens.padding10,
		backgroundColor: Colors.white,
	},
});

export default BottomTabNavigator;
